/**
 * Converts various time formats to .NET TimeSpan format (D.HH:mm:ss)
 * Examples:
 * - "1 day" or "1 days" → "1.00:00:00"
 * - "24 hours" or "24 hour" → "1.00:00:00"
 * - "30 minutes" or "30 minute" → "0.00:30:00"
 * - "1.00:00:00" → "1.00:00:00" (already in correct format)
 * - "1:30:00" → "0.01:30:00" (hours:minutes:seconds)
 *
 * @param input - String input that can be in various formats
 * @returns TimeSpan formatted string (D.HH:mm:ss) or null if input is empty/invalid
 */
export function formatToTimeSpan(
  input: string | null | undefined,
): string | null {
  if (!input || typeof input !== "string") {
    return null;
  }

  const trimmed = input.trim();
  if (trimmed === "") {
    return null;
  }

  const timeSpanRegex = /^(\d+)\.(\d{2}):(\d{2}):(\d{2})$/;
  if (timeSpanRegex.test(trimmed)) {
    return trimmed;
  }

  const timeRegex = /^(\d{1,2}):(\d{2}):(\d{2})$/;
  const timeMatch = trimmed.match(timeRegex);
  if (timeMatch) {
    const hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    const seconds = parseInt(timeMatch[3], 10);
    const totalDays = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${totalDays}.${String(remainingHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  const timeShortRegex = /^(\d{1,2}):(\d{2})$/;
  const timeShortMatch = trimmed.match(timeShortRegex);
  if (timeShortMatch) {
    const hours = parseInt(timeShortMatch[1], 10);
    const minutes = parseInt(timeShortMatch[2], 10);
    const totalDays = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${totalDays}.${String(remainingHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
  }

  const numberUnitRegex =
    /^(\d+(?:\.\d+)?)\s*(day|days|hour|hours|hr|hrs|minute|minutes|min|mins|second|seconds|sec|secs)$/i;
  const numberUnitMatch = trimmed.match(numberUnitRegex);
  if (numberUnitMatch) {
    const value = parseFloat(numberUnitMatch[1]);
    const unit = numberUnitMatch[2].toLowerCase();

    let totalSeconds = 0;
    if (unit.startsWith("day")) {
      totalSeconds = value * 24 * 60 * 60;
    } else if (unit.startsWith("hour") || unit.startsWith("hr")) {
      totalSeconds = value * 60 * 60;
    } else if (unit.startsWith("minute") || unit.startsWith("min")) {
      totalSeconds = value * 60;
    } else if (unit.startsWith("second") || unit.startsWith("sec")) {
      totalSeconds = value;
    }

    const days = Math.floor(totalSeconds / (24 * 60 * 60));
    const remainingSeconds = totalSeconds % (24 * 60 * 60);
    const hours = Math.floor(remainingSeconds / (60 * 60));
    const remainingMinutes = remainingSeconds % (60 * 60);
    const minutes = Math.floor(remainingMinutes / 60);
    const secs = remainingMinutes % 60;

    return `${days}.${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }

  const numberOnlyRegex = /^(\d+(?:\.\d+)?)$/;
  const numberOnlyMatch = trimmed.match(numberOnlyRegex);
  if (numberOnlyMatch) {
    const hours = parseFloat(numberOnlyMatch[1]);
    const totalDays = Math.floor(hours / 24);
    const remainingHours = Math.floor(hours % 24);
    const minutes = Math.floor((hours % 1) * 60);
    return `${totalDays}.${String(remainingHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
  }

  return null;
}
